import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import Modal from "components/common/modal";
import Paging from "components/common/paging";
import Loading from "components/common/loading";
import { getToken } from "context/auth";
import { getPermitHistory } from "api/permits";

import { ReactComponent as ClockIcon } from "assets/icons/ico_clock.svg";

const PAGE_SIZE = 10;

export const PermitHistoryModal = ({ display, setDisplay, permit }) => {
  const { t } = useTranslation();

  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  const fetchHistory = useCallback(async () => {
    if (!permit?.permitExtId) {
      return;
    }

    setIsLoading(true);
    setHasError(false);

    try {
      const token = await getToken();
      const result = await getPermitHistory(token, permit.permitExtId);
      // Stöd både rå lista och { events: [...] }-svar tills API-kontraktet låsts.
      setEvents(Array.isArray(result) ? result : (result?.events ?? []));
    } catch (error) {
      console.error(error);
      setHasError(true);
      setEvents([]);
    } finally {
      setIsLoading(false);
    }
  }, [permit]);

  useEffect(() => {
    if (display) {
      setCurrentPage(0);
      fetchHistory();
    }
  }, [display, fetchHistory]);

  const totalNumberOfPages = Math.ceil(events.length / PAGE_SIZE);
  const pageEvents = events.slice(
    currentPage * PAGE_SIZE,
    currentPage * PAGE_SIZE + PAGE_SIZE
  );

  const licencePlate = permit?.isManualActivation
    ? permit?.manualActivatedLicencePlate
    : permit?.registrationIdentifier;

  const getEventDescription = (event) => {
    switch (event.eventType) {
      case "VehicleChanged":
        return t("permitHistory.vehicleChanged", {
          from: event.oldValue || "—",
          to: event.newValue || "—",
        });
      case "PermitEnded":
        return t("permitHistory.permitEnded");
      case "InviteSent":
        return t("permitHistory.inviteSent");
      default:
        return event.eventType;
    }
  };

  const getActorRoleLabel = (event) =>
    event.actorRole
      ? t(`permitHistory.actor.${event.actorRole}`, { defaultValue: event.actorRole })
      : null;

  // Vem som utförde åtgärden: namn först, annars e-post (rollen visas separat).
  // Kollapsar ev. dubbla mellanslag som kan finnas i lagrade namn.
  const getActorIdentity = (event) => {
    const name = event.actorName ? event.actorName.replace(/\s+/g, " ").trim() : null;
    return name || event.actorEmail || null;
  };

  return (
    <Modal
      title={t("permitHistory.title")}
      overlayClassName={"items-center justify-center"}
      modalClassName={"max-h-[96vh] overflow-y-auto flex-col z-50"}
      innerClassName={"w-[95vw] md:w-[40vw] overflow-y-auto"}
      display={display}
      setDisplay={setDisplay}
    >
      {/* Valt tillstånd – så det framgår vilket tillstånd loggen gäller */}
      <div
        className="flex flex-col gap-1 mb-4 p-3 rounded-lg bg-airpark-gray-300"
        data-testid="permit-history-selected"
      >
        <span className="text-sm text-airpark-gray-500">
          {t("permitHistory.selectedPermit")}
        </span>
        <span className="font-medium">{permit?.permitExtId}</span>
        <div className="flex flex-row flex-wrap gap-x-4 text-sm">
          {licencePlate && (
            <span>
              {t("permitTable.licensePlateNumber")}: {licencePlate}
            </span>
          )}
          {permit?.friendlyPeriod && <span>{permit.friendlyPeriod}</span>}
        </div>
      </div>

      {/* Logg */}
      <div className="flex flex-col" data-testid="permit-history-list">
        {isLoading && (
          <div className="flex justify-center items-center py-8">
            <Loading size={8} />
          </div>
        )}

        {!isLoading && hasError && (
          <span className="py-8 text-center text-airpark-gray-500">
            {t("permitHistory.error")}
          </span>
        )}

        {!isLoading && !hasError && events.length === 0 && (
          <span className="py-8 text-center text-airpark-gray-500">
            {t("permitHistory.empty")}
          </span>
        )}

        {!isLoading &&
          !hasError &&
          pageEvents.map((event, i) => (
            <div
              key={i}
              className="flex flex-row items-start gap-3 py-3 border-b border-airpark-gray-300"
              data-testid="permit-history-row"
            >
              <ClockIcon className="h-5 w-5 mt-1 shrink-0" />
              <div className="flex flex-col">
                <span className="font-medium">{getEventDescription(event)}</span>
                {(getActorIdentity(event) || getActorRoleLabel(event)) && (
                  <span className="text-sm" data-testid="permit-history-actor">
                    {t("permitHistory.by")}{" "}
                    <span className="font-medium">
                      {getActorIdentity(event) || getActorRoleLabel(event)}
                    </span>
                    {getActorIdentity(event) && getActorRoleLabel(event) ? (
                      <span className="text-airpark-gray-500">
                        {" "}
                        · {getActorRoleLabel(event)}
                      </span>
                    ) : null}
                  </span>
                )}
                <span className="text-xs text-airpark-gray-500">
                  {moment.utc(event.occurredAtUtc).local().format("YYYY-MM-DD HH:mm")}
                </span>
              </div>
            </div>
          ))}
      </div>

      {!isLoading && !hasError && totalNumberOfPages > 1 && (
        <Paging
          totalNumberOfPages={totalNumberOfPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      )}
    </Modal>
  );
};

export default PermitHistoryModal;
